import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import HeroSection from '../components/HeroSection';
// import OurVisionSection from '../components/OurVisionSection';
import TheStorySection from '../components/TheStorySection';
import ContactSection from '../components/ContactSection';
import FeatureTicker from '../components/FeatureTicker';
import BranchesSection from '../components/BranchesSection';
import TestimonialsSection from '../components/TestimonialsSection';
import QASection from '../components/QASection';
import ProductCard from '../components/ProductCard';
import { NavLink, QAItem, Branch, ContactFormData } from '../types';
import { saveContactToGoogleSheets } from '../utils/googleSheetsContact';
import logoImage from '../assets/airy.webp'; // לוגו לתפריט הניווט
import giftGoodImage from '../assets/giftGood.webp';
import pointSaleIcon from '../assets/pointSale.svg';
import officialImporterBadge from '../assets/official-importer-badge.png';
import { colors, typography } from '../styles/designTokens';

// Gift Text Overlay Component - כל משפט עם אפקט מגניב
const GiftTextOverlayWithAnimations: React.FC = () => {
  const [isVisible, setIsVisible] = React.useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    const currentContainer = containerRef.current;
    if (currentContainer) {
      observer.observe(currentContainer);
    }

    return () => {
      if (currentContainer) {
        observer.unobserve(currentContainer);
      }
    };
  }, []);

  const sentences = [
    { text: 'מתנה מקורית,', delay: 0.2, from: 'top', color: '#FFFFFF' },
    { text: 'חדשנית ושימושית.', delay: 0.3, from: 'top', color: '#FFFFFF' },
    { text: 'מתאימה במיוחד', delay: 0.4, from: 'right', color: '#FFFFFF' },
    { text: 'לוועדי עובדים, לתורמים,', delay: 0.5, from: 'right', color: '#FFFFFF' },
    { text: 'ולכל מי שרוצים להוקיר.', delay: 0.6, from: 'left', color: 'gradientRich' }, // gradientRich כמו בנקודות מכירה
  ];
  
  // משפט נפרד בצד שמאל - "מחיר מיוחד לכמויות"
  const leftSideSentence = { text: 'מחיר מיוחד לכמויות', delay: 0.8, from: 'left', color: '#F5B658' }; // כתום כמו בתפריט העליון

  const getInitialTransform = (from: string) => {
    switch (from) {
      case 'top': return 'translateY(-60px) scale(0.7) rotate(-5deg)';
      case 'bottom': return 'translateY(60px) scale(0.7) rotate(5deg)';
      case 'right': return 'translateX(60px) scale(0.7) rotate(5deg)';
      case 'left': return 'translateX(-60px) scale(0.7) rotate(-5deg)';
      default: return 'scale(0.7)';
    }
  };

  return (
    <div
      ref={containerRef}
      style={{
        position: 'absolute',
        top: 0,
        bottom: 0,
        left: 0,
        right: 0,
        transform: 'none',
        zIndex: 10,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '0 clamp(20px, 5vw, 60px)',
        width: '100%',
        boxSizing: 'border-box',
      }}
    >
      {/* צד שמאל - המשפטים הראשיים */}
      <div
        style={{
          textAlign: 'right',
          direction: 'rtl',
          maxWidth: 'clamp(280px, 35vw, 450px)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'clamp(4px, 0.5vw, 8px)',
        }}
      >
        {sentences.map((sentence, index) => (
          <p
            key={index}
            className="font-bold"
            style={{
              fontSize: 'clamp(1.6rem, 3.8vw, 2.3rem)', // הגדלתי את הכיתוב
              letterSpacing: typography.body.letterSpacing,
              ...(sentence.color === 'gradientRich' ? {
                background: colors.accentSpecial.textGradientRich,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              } : sentence.color === 'gradient' ? {
                background: colors.accentSpecial.textGradientStripes,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              } : {
                color: sentence.color,
              }),
              lineHeight: 1.3,
              margin: 0,
              marginBottom: index === 1 ? 'clamp(1.8rem, 4.5vw, 2.8rem)' : '0', // 3 שורות רווח אחרי "חדשנית ושימושית."
              padding: 0,
              textShadow: '0 2px 8px rgba(0, 0, 0, 0.5), 0 0 20px rgba(245, 182, 88, 0.6)', // צל וזוהר כתום
              opacity: isVisible ? 1 : 0,
              transform: isVisible 
                ? 'translateX(0) translateY(0) scale(1) rotate(0deg)' 
                : getInitialTransform(sentence.from),
              filter: isVisible ? 'blur(0px)' : 'blur(15px)',
              transition: isVisible 
                ? `opacity 0.9s cubic-bezier(0.34, 1.56, 0.64, 1) ${sentence.delay}s, transform 0.9s cubic-bezier(0.34, 1.56, 0.64, 1) ${sentence.delay}s, filter 0.9s ease-out ${sentence.delay}s`
                : 'none',
            }}
          >
            {sentence.text}
          </p>
        ))}
      </div>

      {/* תגית טקסט מסתובבת - צד שמאל תחתון מעל התמונה */}
      <div
        style={{
          textAlign: 'right',
          direction: 'rtl',
          position: 'absolute',
          left: 'clamp(120px, 16vw, 280px)',
          bottom: 'clamp(160px, 16vw, 280px)',
          zIndex: 11,
        }}
      >
        <div
          className="rotating-text-badge"
          style={{
            opacity: isVisible ? 1 : 0,
            transform: isVisible 
              ? 'translateX(0) translateY(0) scale(1) rotate(0deg)' 
              : getInitialTransform(leftSideSentence.from),
            filter: isVisible ? 'blur(0px)' : 'blur(15px)',
            transition: isVisible 
              ? `opacity 0.9s cubic-bezier(0.34, 1.56, 0.64, 1) ${leftSideSentence.delay}s, transform 0.9s cubic-bezier(0.34, 1.56, 0.64, 1) ${leftSideSentence.delay}s, filter 0.9s ease-out ${leftSideSentence.delay}s`
              : 'none',
            zIndex: 12,
            pointerEvents: 'auto',
            position: 'relative',
            width: 'clamp(170px, 20vw, 240px)',
            height: 'clamp(170px, 20vw, 240px)',
          }}
          aria-hidden="true"
        >
          {/* עיגול פנימי עם מסגרת ואור מהמהם */}
          <div
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '135px',
              height: '135px',
              borderRadius: '50%',
              border: '2px solid rgba(245, 182, 88, 0.8)',
              background: 'transparent',
              boxShadow: `
                inset 0 0 35px rgba(245, 182, 88, 0.25),
                inset 0 0 60px rgba(245, 182, 88, 0.15)
              `,
              animation: 'pulseGlowOrange 2.5s ease-in-out infinite',
              zIndex: 1,
            }}
          />
          <svg viewBox="0 0 200 200" aria-hidden="true" style={{ position: 'relative', zIndex: 2 }}>
            <defs>
              <path
                id="badgePath"
                d="M 100, 100 m -75, 0 a 75,75 0 1,1 150,0 a 75,75 0 1,1 -150,0"
              />
            </defs>
            <text direction="rtl" unicodeBidi="plaintext" style={{ fontSize: '18px' }}>
              <textPath href="#badgePath" xlinkHref="#badgePath" startOffset="50%" textAnchor="middle">
                <tspan fill="#FFFFFF" fontWeight="700">מחיר מיוחד לכמויות</tspan>
                <tspan fill="#F5B658" fontWeight="900"> • </tspan>
                <tspan fill="#FFFFFF" fontWeight="700">מתנה פרקטית ואיכותית</tspan>
                <tspan fill="#F5B658" fontWeight="900"> •</tspan>
              </textPath>
            </text>
          </svg>
        </div>
      </div>
    </div>
  );
};

const HomePage = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);
  const showLegacyVision = false; // toggle to show/hide the legacy vision section

  const handleContactSubmit = async (formData: ContactFormData) => {
    try {
      // שליחה ל-Google Sheets
      await saveContactToGoogleSheets(formData);
      // הצלחה - הטופס כבר מציג הודעה דרך ContactForm.tsx
    } catch (error) {
      // כשלון - הטופס כבר מציג הודעה דרך ContactForm.tsx
      // זורק את השגיאה כדי שהטופס יוכל לטפל בה
      console.error('Error saving contact form:', error);
      throw error;
    }
  };

  const navLinks: NavLink[] = [
    { label: 'בית', href: '#home', isActive: true },
    { label: 'ממליצים', href: '#testimonials', isActive: false },
    { label: 'מפרט המכונה', href: '#product-specs', isActive: false },
    { label: 'נקודות מכירה', href: '#branches', isActive: false },
    { label: 'צור קשר', href: '#contact', isActive: false },
  ];

  // Testimonials data is now loaded from JSON file in TestimonialsSection component

  const qaItems: QAItem[] = [
              {
                question: 'האם המנוע חזק מספיק?',
                answer: 'אכן, קיימים 2 מנועים עוצמתיים, לביצוע 2 פעולות בו זמנית, תספורת ושאיבה.',
              },
              {
                question: 'האם המכונה שואבת את כל השיער הקצוץ?',
                answer: 'המכונה שואבת עד 97% מהשיער הקצוץ תוך כדי עבודה.\nלניצול מקסימלי של עוצמת השאיבה, מומלץ לספר בקצב נינוח ומבוקר.',
              },
              {
                question: 'כל כמה זמן צריך לרוקן את המיכל?',
                answer: 'זה משתנה לפי כמות וסוג השיער, אבל התפעול כל כך פשוט שזה לא עוצר את הקצב: בלחיצת כפתור מכבים את המכונה, מטים אותה כלפי מטה והשיער נשפך החוצה. במקרים של עומס במיכל, ניתן לשלוף את המיכל לריקון יסודי בשניות. פשוט, נקי וממשיכים בתספורת.',
              },
              {
                question: 'האם הסכין איכותית וחדה מספיק?',
                answer: 'הסכין איכותית מאוד, חדה וקוצצת היטב, עם זאת היא עדינה ונעימה למסתפר באופן יוצא מן הרגיל.',
              },
              {
                question: 'מהם הפריטים הנלווים למכונה?',
                answer: '4 מסרקי הגבהה, כבל הטענה באורך 2 מטר, ראש מטען, בקבוקון שמן, מברשת ניקוי.',
              },
  ];




  const branchCards: Branch[] = [
    // ירושלים
    { city: 'ירושלים', store: 'גוֹטְלִיבּ׳ס תשמישי קדושה', address: 'מאה שערים 36' },
    { city: 'ירושלים', store: 'NK חשמל וסלולר', address: 'מוסיוף 8' },
    { city: 'ירושלים', store: 'רפאלי', address: 'עמוס 6' },
    { city: 'ירושלים', store: 'יריד ביתר - רמות', address: 'הרב מ.ל. רובין 23' },

    // בני ברק
    { city: 'בני ברק', store: 'טמבור המחסן', address: 'אמרי ברוך 1' },
    { city: 'בני ברק', store: 'טכנו מיקס', address: 'הרב קוק 16' },
    { city: 'בני ברק', store: 'UNR', address: 'אלישע 5' },
    { city: 'בני ברק', store: 'רפאלי', address: '  עזרא 68, רבי עקיבא 115' },
    { city: 'בני ברק', store: 'טמבור המרכז', address: 'חזון איש 49' },
    { city: 'בני ברק', store: 'הגפן-הכל לבית היהודי', address: 'אהבת שלום 16' },

    // אשדוד
    { city: 'אשדוד', store: 'אינדקס', address: 'רשבי 13' },
    // אלעד
    { city: 'אלעד', store: 'רפאלי', address: 'יהודה הנשיא 94' },
    // בית שמש
    { city: 'בית שמש', store: 'בדק בית', address: 'יהודה הנשיא 5' },
    { city: 'בית שמש', store: 'כל בו האמוראים', address: 'האמוראים 92' },
    { city: 'בית שמש', store: 'כל בו מנדל', address: 'בן איש חי 29' },
    { city: 'בית שמש', store: 'הכי טוב לבית', address: 'חזון איש 35' },
    { city: 'בית שמש', store: 'רפאלי', address: 'נחל ניצנים 12' },
    // ביתר
    { city: 'ביתר', store: 'כל בו רוטשילד', address: 'הר"ן 5 , בבא סאלי 2' },
    { city: 'ביתר', store: 'יריד ביתר', address: 'אלעזר המודעי 7, הר"ן 14' },

    // קרית גת
    { city: 'קרית גת', store: 'כל בו רוטשילד', address: 'האדמור מחב"ד 9' },
    // חצור הגלילית
    { city: 'חצור הגלילית', store: 'הבורג שלי', address: '' },
    // נתניה
    { city: 'נתניה', store: 'צבי סנטר', address: 'רבי עובדיה מברטנורא 1' },
    // חיפה
    { city: 'חיפה', store: 'מקור החשמל', address: 'הרצל 64' },
    // דימונה
    { city: 'דימונה', store: 'תלת פאזי', address: 'יגאל אלון 221' },
    // מודיעין עילית
    { city: 'מודיעין עילית', store: 'בית החשמל', address: 'רשב"י 30' },
    // דרום → ערד
    { city: 'ערד', store: 'שרגאור', address: 'שמעון 38' },
    // חריש
    { city: 'חריש', store: 'בתיאום', address: 'בטלפון: 0527185337' },
    // נוף הגליל
    { city: 'נוף הגליל', store: 'שרגאספקה', address: 'האדמו"ר מבעלזא 3' },
    // עפולה
    { city: 'עפולה', store: 'ספינר', address: 'שדה יצחק 48' },
  ];


  return (
    <div
      className="min-h-screen scroll-smooth"
                style={{ 
        backgroundColor: colors.background,
        fontFamily: typography.fontFamily,
        overflowX: 'hidden', // מונע גלילה אופקית
        width: '100%',
        maxWidth: '100%',
        paddingTop: 'clamp(53px, calc(6vw + 3px), 63px)', // מקום לאידר הקבוע: פס עליון (3px) + גובה האידר (50px-60px)
      }}
    >
      {/* Header Component */}
      <Header
        logoImage={logoImage}
        navLinks={navLinks}
        isMobileMenuOpen={isMobileMenuOpen}
        onMobileMenuToggle={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
      />

      {/* Main Content - נגישות */}
      <main id="main-content">
        {/* Hero Section - Component */}
        <HeroSection logoImage={logoImage} />

      {/* Animated Line - רץ בין Hero ל-Story - פס מונפש */}
      <div
        style={{
          position: 'relative',
          width: '100%',
          height: '4px',
            padding: 0,
          margin: 0,
            zIndex: 10,
            pointerEvents: 'none',
            overflow: 'hidden',
          }}
        >
          <div
            className="animated-line-container"
            style={{
              width: '100%',
            height: '100%',
              position: 'relative',
              overflow: 'hidden',
              margin: 0,
              backgroundColor: 'transparent',
            }}
          >
            <div
              className="animated-line"
              style={{
                position: 'absolute',
                width: '200%',
                height: '100%',
                background: `linear-gradient(90deg, 
                  ${colors.gold.darker} 0%,
                  ${colors.gold.mediumDark} 12%,
                  ${colors.gold.base} 28%,
                  ${colors.gold.lighter} 50%,
                  ${colors.gold.base} 72%,
                  ${colors.gold.mediumDark} 88%,
                  ${colors.gold.darker} 100%
                )`,
              animation: 'slideLineLeft 1.5s linear infinite',//שינוי מהירות הפס שרץ
                willChange: 'transform',
                boxShadow: '0 0 12px rgba(245, 182, 88, 0.7), 0 0 20px rgba(255, 255, 255, 0.5)',
                filter: 'brightness(1.2)',
              }}
            />
          </div>
        </div>

      {/* Our Vision Section - Component (legacy, toggled) */}
      {/* {showLegacyVision && <OurVisionSection giftImage="" />} */} 

      {/* The Story of Airy Section - Component */}
      <TheStorySection machineImage="" />

      {/* Animated Line - מתחת למקטע השלאו והתושוב - פס סטטי - צמוד לחלק */}
        {/* //פס סטיי בגוניים מעל הפס של האיקונים */}
      <div
        style={{
          width: '100%',
          height: '4px',
          background: `linear-gradient(90deg, 
            ${colors.gold.darker} 0%,
            ${colors.gold.mediumDark} 12%,
            rgba(0, 0, 0, 0.95) 30%,
            rgba(0, 0, 0, 1) 50%,
            rgba(0, 0, 0, 0.95) 70%,
            ${colors.gold.mediumDark} 88%,
            ${colors.gold.darker} 100%
          )`,
          margin: 0, // ללא רווח - צמוד לחלק
          boxShadow: '0 0 12px rgba(245, 182, 88, 0.7), 0 0 20px rgba(255, 255, 255, 0.5)',
          filter: 'brightness(1.2)',
        }}
      />

      {/* Advantages of Airy Section - Component */}
      <FeatureTicker />

      {/* Animated Line - מתחת למקטע היתרונות - פס סטטי - צמוד לחלק */}
      <div
        style={{
          width: '100%',
          height: '4px',
          background: `linear-gradient(90deg, 
            ${colors.gold.darker} 0%,
            ${colors.gold.mediumDark} 12%,
            rgba(0, 0, 0, 0.95) 30%,
            rgba(0, 0, 0, 1) 50%,
            rgba(0, 0, 0, 0.95) 70%,
            ${colors.gold.mediumDark} 88%,
            ${colors.gold.darker} 100%
          )`,
          margin: 0, // ללא רווח - צמוד לחלק
          boxShadow: '0 0 12px rgba(245, 182, 88, 0.7), 0 0 20px rgba(255, 255, 255, 0.5)',
          filter: 'brightness(1.2)',
        }}
      />

      {/* Testimonials Section - Component */}
      <TestimonialsSection />

      {/* Official Importer Badge - בין ממליצים לשאלות - מקביל לכפתור ולכותרת - על שני הרקעים */}
      <div
        style={{
          position: 'relative',
          height: '0',
          overflow: 'visible',
          zIndex: 100,
        }}
      >
        <div
          style={{
            position: 'absolute',
            right: 'clamp(20px, 4vw, 60px)',
            top: 'clamp(-50px, 5vh, 50px)',
            transform: 'translateY(-50%) rotate(15deg)',
            zIndex: 100,
          }}
        >
          <img
            src={officialImporterBadge}
            alt="יבואן רשמי של מכונות תספורת איירי"
            width="300"
            height="266"
            style={{
              width: 'clamp(100px, 18vw, 250px)',
              height: 'auto',
              display: 'block',
              filter: 'brightness(0) invert(1) drop-shadow(0 2px 6px rgba(0, 0, 0, 0.2))',
            }}
            loading="lazy" // 🚀 PERFORMANCE FIX: טעינה עצלה - התמונה תיטען רק כשמגיעים אליה
          />
        </div>
      </div>

      {/* Animated Line - מעל Q&A - פס מונפש זהה לפס העליון */}
      {/* //פס מעל לחלק של שאלות ותושוובת */}
      {/* <div
        style={{
          position: 'relative',
          width: '100%',
          height: '4px',
          padding: 0,
          margin: '10px 0 0 0',
          zIndex: 10,
          pointerEvents: 'none',
          overflow: 'hidden',
        }}
      >
        <div
          className="animated-line-container"
          style={{
            width: '100%',
            height: '100%',
            position: 'relative',
            overflow: 'hidden',
            margin: 0,
            backgroundColor: 'transparent',
          }}
        >
          <div
            className="animated-line"
            style={{
              position: 'absolute',
              width: '200%',
              height: '100%',
              background: `linear-gradient(90deg, 
                ${colors.gold.darker} 0%,
                ${colors.gold.mediumDark} 12%,
                ${colors.gold.base} 28%,
                ${colors.gold.lighter} 50%,
                ${colors.gold.base} 72%,
                ${colors.gold.mediumDark} 88%,
                ${colors.gold.darker} 100%
              )`,
              animation: 'slideLineLeft 0.9s linear infinite',
              willChange: 'transform',
              boxShadow: '0 0 12px rgba(245, 182, 88, 0.7), 0 0 20px rgba(255, 255, 255, 0.5)',
              filter: 'brightness(1.2)',
            }}
          />
        </div>
      </div> */}

      {/* Q&A Section - Component */}
      <QASection items={qaItems} logoImage={logoImage} />

      {/* Gift Image Section - תמונת מתנה עם Parallax Effect (background-attachment: fixed) */}
      <section 
        className="relative gift-parallax-section z-10 overflow-hidden"
        style={{ 
          // Parallax Effect - תמונת המתנה מקובעת בזמן גלילה
          backgroundImage: `url(${giftGoodImage})`,
          backgroundAttachment: 'fixed', // הקסם! מקבע את הרקע ביחס ל-viewport
          backgroundSize: 'cover', // מכסה את כל הקטע
          backgroundPosition: 'center center', // מרכז את התמונה
          backgroundRepeat: 'no-repeat',
          minHeight: '100vh', // גובה מינימלי - מאפשר הרחבה ב-zoom
          width: '100%',
          position: 'relative', // כדי שה-overlay יעבוד
          overflow: 'hidden',
          // הוספת padding-bottom כדי שהתמונה תיראה גם מתחת למקטע החופף
          paddingBottom: '80px', // מקום למקטע החופף
        }}
      >
        {/* Text Overlay - טקסט בצד ימין על התמונה - כל משפט עם אפקט מגניב */}
        <GiftTextOverlayWithAnimations />
      </section>

      {/* Branches Section - Component */}
      <BranchesSection 
        branches={branchCards}
        pointSaleIcon={pointSaleIcon}
      />

      {/* Animated Line - מעל מקטע יצירת קשר - פס סטטי */}
      <div
        style={{
          width: '100%',
          height: '4px',
          background: `linear-gradient(90deg, 
            transparent 0%, 
            ${colors.gold.darker} 5%,
            ${colors.gold.mediumDark} 25%,
            ${colors.gold.base} 45%, 
            ${colors.gold.mediumLight} 65%,
            ${colors.gold.lighter} 85%,
            transparent 100%
          )`,
          margin: 'clamp(20px, 3vw, 40px) 0',
          boxShadow: '0 0 12px rgba(245, 182, 88, 0.7), 0 0 20px rgba(255, 255, 255, 0.5)',
          filter: 'brightness(1.2)',
        }}
      />

      {/* Contact Section - Component */}
      <ContactSection 
        onSubmit={handleContactSubmit}
      />
      </main>

      {/* Footer Component */}
      <Footer />
    </div>
  );
};

export default HomePage;

